import {
  validateEditorEn,
  validateTextEn,
  EMPTY_FIELD_MESSAGE,
  validateSpace,
  validateSpaceTextInput,
} from '@Utils/validation';
import { z } from 'zod';

enum CORPORATE_INFORMATION_MESSAGE {
  COMPANY = 'กรุณาระบุชื่อบริษัท',
  COMPANY_EN = 'กรุณาระบุชื่อบริษัทเป็นภาษาอังกฤษ',
  SYMBOL = 'กรุณาระบุชื่อย่อหลักทรัพย์',
  ADDRESS = 'กรุณาระบุสถานที่ตั้งสำนักงานใหญ่',
  ADDRESS_EN = 'กรุณาระบุสถานที่ตั้งสำนักงานใหญ่เป็นภาษาอังกฤษ',
  PROVINCE = 'กรุณาระบุจังหวัด',
  PROVINCE_EN = 'กรุณาระบุจังหวัดเป็นภาษาอังกฤษ',
  ZIPCODE = 'กรุณาระบุรหัสไปรษณีย์',
  ZIPCODE_NUMBER = 'กรุณาระบุรหัสไปรษณีย์ให้ถูกต้อง',
  BUSINESS_TYPE = 'กรุณาระบุประเภทธุรกิจ',
  BUSINESS_TYPE_EN = 'กรุณาระบุประเภทธุรกิจเป็นภาษาอังกฤษ',
  REGISTERED_NUMBER = 'กรุณาระบุเลขทะเบียนบริษัท',
  REGISTERED_NUMBER_TYPE = 'กรุณาระบุเลขทะเบียนบริษัทให้ถูกต้อง',
  WEBSITE = 'กรุณาระบุเว็บไซต์',
  TELEPHONE = 'กรุณาระบุหมายเลขโทรศัพท์',
  EMAIL = 'กรุณาระบุอีเมล',
  COMPLETE_INFORMATION = 'กรุณากรอกข้อมูลให้ครบถ้วน',
}

enum REMARK_MESSAGE {
  DESCRIPTION_EN = 'กรุณาระบุหมายเหตุเป็นภาษาอังกฤษ',
}

enum INTRO_MESSAGE {
  DESCRIPTION_EN = 'กรุณาระบุคำบรรยายเป็นภาษาอังกฤษ',
}

const IntroductionFormSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateEditorEn, {
      message: INTRO_MESSAGE.DESCRIPTION_EN,
    })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const RemarkFormSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateTextEn, {
      message: REMARK_MESSAGE.DESCRIPTION_EN,
    })
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

export const CompleteSectionSchema = z.object({
  isCompleteTh: z.boolean({
    message: CORPORATE_INFORMATION_MESSAGE.COMPLETE_INFORMATION,
  }),
  isCompleteEn: z.boolean({
    message: CORPORATE_INFORMATION_MESSAGE.COMPLETE_INFORMATION,
  }),
});

const ThaiCorporateInformationSchema = z.object({
  companyTh: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.COMPANY })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.COMPANY })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  symbolTh: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.SYMBOL })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.SYMBOL })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  addressTh: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.ADDRESS })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.ADDRESS })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  provinceTh: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.PROVINCE })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.PROVINCE })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  zipcodeTh: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.ZIPCODE })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.ZIPCODE })
    .regex(/^\d+$/, { message: CORPORATE_INFORMATION_MESSAGE.ZIPCODE_NUMBER }),
  businessTypeTh: z
    .string({
      message: CORPORATE_INFORMATION_MESSAGE.BUSINESS_TYPE,
    })
    .min(1, {
      message: CORPORATE_INFORMATION_MESSAGE.BUSINESS_TYPE,
    })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  registeredNumberTh: z
    .string({
      message: CORPORATE_INFORMATION_MESSAGE.REGISTERED_NUMBER,
    })
    .min(1, {
      message: CORPORATE_INFORMATION_MESSAGE.REGISTERED_NUMBER,
    })
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  telephoneTh: z
    .string({
      message: CORPORATE_INFORMATION_MESSAGE.TELEPHONE,
    })
    .min(1, {
      message: CORPORATE_INFORMATION_MESSAGE.TELEPHONE,
    })
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  faxTh: z
    .string()
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .optional(),
  websiteTh: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.WEBSITE })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.WEBSITE })
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  emailTh: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.EMAIL })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.EMAIL })
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR),
});

export const EnglishCorporateInformationSchema = z.object({
  companyEn: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.COMPANY })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.COMPANY })
    .regex(validateTextEn, CORPORATE_INFORMATION_MESSAGE.COMPANY_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  symbolEn: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.SYMBOL })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.SYMBOL })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  addressEn: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.ADDRESS })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.ADDRESS })
    .regex(validateTextEn, CORPORATE_INFORMATION_MESSAGE.ADDRESS_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  zipcodeEn: z.string().optional().nullable(),
  businessTypeEn: z
    .string({ message: CORPORATE_INFORMATION_MESSAGE.BUSINESS_TYPE })
    .min(1, { message: CORPORATE_INFORMATION_MESSAGE.BUSINESS_TYPE })
    .regex(validateTextEn, CORPORATE_INFORMATION_MESSAGE.BUSINESS_TYPE_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  registeredNumberEn: z.string().optional().nullable(),
  telephoneEn: z.string().optional().nullable(),
  websiteEn: z.string().optional().nullable(),
  emailEn: z.string().optional().nullable(),
});

export const CombinedCorporateInformationSchema = z.object({
  thai: ThaiCorporateInformationSchema,
  english: EnglishCorporateInformationSchema,
  completeSection: CompleteSectionSchema.optional(),
  introduction: IntroductionFormSchema.optional(),
  remark: RemarkFormSchema.optional(),
});
